import { i18n } from "@/i18n";
import cloneDeep from "lodash/cloneDeep";
import Nprogress from "nprogress";
import moment from "moment-timezone";
import orderBy from "lodash/orderBy";
import VueNotifications from "vue-notifications";
import { meetingAPI } from "@/api";
import MeetingPermissions from "@/permissions/MeetingPermissions";
import OutcomePermissions from "@/permissions/OutcomePermissions";
import debugWrapper from "@/helpers/debugWrapper";

const MILLISECONDS = 1000;
const MEETING_SERIES_PAGE_SIZE = 10;

function calculateNewPastMeetingsState( { meetings, rootGetters, } ) {
	const timezone = rootGetters[ "auth/timezone" ];
	const userProfile = rootGetters[ "userProfile/userProfile" ];
	return meetings.reduce( ( accum, meeting ) => {
		const meetingPermissions = new MeetingPermissions( { meeting, userProfile, timezone, } );
		meeting.permissions = meetingPermissions.resolve();
		accum[ meeting.id ] = meeting;
		return accum;
	}, {} );
}

const _state = {
	seriesMap: {},
};

const _getters = {
	hasInitiallyLoaded: state => meetingSeriesId => {
		const meta = state.seriesMap[ meetingSeriesId ];
		return meta.hasInitiallyLoaded;
	},
	hasLoadedAll: state => meetingSeriesId => {
		const meta = state.seriesMap[ meetingSeriesId ];
		return meta.hasLoadedAll;
	},
	hasLoadedTopics: state => ( meetingSeriesId, meetingId ) => {
		const meta = state.seriesMap[ meetingSeriesId ];
		return meta.meetingLoadedState[ meetingId ].hasLoadedTopics;
	},
	isLoading: state => meetingSeriesId => {
		const meta = state.seriesMap[ meetingSeriesId ];
		return meta.isLoading;
	},
	isLoadingTopics: state => ( meetingSeriesId, meetingId ) => {
		const meta = state.seriesMap[ meetingSeriesId ];
		return meta.meetingLoadedState[ meetingId ].isLoadingTopics;
	},
	meetingSeriesHeaderDateFormat: () => "MMMM D, YYYY", // April 20, 2020
	pastMeetings: state => meetingSeriesId => {
		const meta = state.seriesMap[ meetingSeriesId ];
		return orderBy(
			Object.values( meta.meetings ),
			[ "plannedStart", ],
			[ "desc", ]
		);
	},
	seriesMeta: state => meetingSeriesId => {
		return state.seriesMap[ meetingSeriesId ];
	},
};

const actions = debugWrapper( "teaming:store:pastMeetings:actions", {
	changeCommitmentCompletion: async ( { dispatch, }, { meetingSeriesId, meetingId, topicId, commitmentId, isCompleted, } ) => {
		Nprogress.start();
		try {
			await meetingAPI.saveOutcomeCompletion( {
				meetingId,
				topicId,
				outcomeId: commitmentId,
				isCompleted,
			}, { version: "2", } );
			// NOTE - I'm holding off on updating the commitment client side & instead
			// am waiting on it to return from the API call below to keep the size of
			// the current change set "smaller" than what it would be otherwise
			dispatch( "loadMeeting", { meetingSeriesId, meetingId, } );
		} catch ( e ) {
			const message = i18n.t( "commitmentsStore.store_updateCommitmentError", {
				message: e.message,
			} );
			VueNotifications.error( { message, } );
			console.error( message, e );
		}
		Nprogress.done();
	},
	initializePastMeetings: async ( { commit, dispatch, }, { meetingSeriesId, plannedStart, } ) => {
		const meta = {
			plannedStart,
			meetings: {},
			meetingLoadedState: {},
			hasInitiallyLoaded: false,
			hasLoadedAll: false,
			isLoading: false,
			currentOffset: 0,
		};
		commit( "seriesMetaUpdated", { meetingSeriesId, meta, } );
		setImmediate( () => { dispatch( "loadPageOfPastMeetings", { meetingSeriesId, } ); } );
	},
	loadMeeting: async ( { commit, getters, rootGetters, }, { meetingSeriesId, meetingId, } ) => {
		let meta = cloneDeep( getters.seriesMeta( meetingSeriesId ) );
		try {
			meta.meetingLoadedState[ meetingId ].isLoadingTopics = true;
			commit( "seriesMetaUpdated", { meta, meetingSeriesId, } );

			const meetingPermissions = meta.meetings[ meetingId ].permissions;
			// cloning the response data to prevent shared state mutation if piggyBack is used in the future
			const { topics, } = cloneDeep( await meetingAPI.getMeeting( { meetingId, }, { version: 2, } ) );

			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];

			// iterate over all outcomes so that we can apply an OutcomePermissions instance
			topics.forEach( topic => {
				topic.outcomes.forEach( outcome => {
					const perms = new OutcomePermissions( {
						outcome,
						userProfile,
						timezone,
						meetingPermissions,
					} );
					outcome.permissions = perms;
				} );
			} );

			// refresh state since we just completed an async operation
			meta = cloneDeep( getters.seriesMeta( meetingSeriesId ) );
			meta.meetings[ meetingId ].topics = topics;
			meta.meetingLoadedState[ meetingId ].isLoadingTopics = false;
			meta.meetingLoadedState[ meetingId ].hasLoadedTopics = true;
			commit( "seriesMetaUpdated", { meta, meetingSeriesId, } );
		} catch ( e ) {
			const message = i18n.t( "commitmentsStore.store_loadMeetingError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
	},
	loadPageOfPastMeetings: async ( { commit, getters, rootGetters, }, { meetingSeriesId, } ) => {
		let meta = cloneDeep( getters.seriesMeta( meetingSeriesId ) );
		try {
			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];
			const offset = meta.currentOffset;
			const plannedStartFrom = moment.tz( meta.plannedStart * MILLISECONDS, timezone ).format( "YYYY-MM-DD" );

			const { value: meetings, } = await meetingAPI.fetchMeetingSeries( {
				meetingSeriesId,
				plannedStartFrom,
				offset,
				limit: MEETING_SERIES_PAGE_SIZE,
			} );

			meetings.forEach( meeting => {
				const permissions = new MeetingPermissions( { meeting, userProfile, timezone, } );
				meeting.permissions = permissions.resolve();
			} );

			// refresh state since we just completed an async operation
			meta = cloneDeep( getters.seriesMeta( meetingSeriesId ) );
			if ( meetings.length ) {
				meetings.forEach( m => {
					meta.meetingLoadedState[ m.id ] = { hasLoadedTopics: false, isLoadingTopics: false, };
				} );
				meta.meetings = {
					...meta.meetings,
					...calculateNewPastMeetingsState( { meetings, rootGetters, } ),
				};
				meta.currentOffset += MEETING_SERIES_PAGE_SIZE;
			}
			if ( meetings.length < MEETING_SERIES_PAGE_SIZE ) {
				meta.hasLoadedAll = true;
			}
			meta.isLoading = false;
			meta.hasInitiallyLoaded = true;
			commit( "seriesMetaUpdated", { meta, meetingSeriesId, } );
		} catch ( e ) {
			meta.isLoading = false;
			commit( "seriesMetaUpdated", { meta, meetingSeriesId, } );
			const message = i18n.t( "commitmentsStore.store_loadPageOfPastMeetingsError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
	},
}, { skipFirstArg: true, } );

const mutations = {
	seriesMetaUpdated: ( state, { meetingSeriesId, meta, } ) => {
		const seriesMap = state.seriesMap;
		state.seriesMap = {
			...seriesMap,
			...{ [ meetingSeriesId ]: cloneDeep( meta ), },
		};
	},
};

export default {
	state: _state,
	getters: _getters,
	actions,
	mutations,
	namespaced: true,
};



// WEBPACK FOOTER //
// ./src/store/modules/pastMeetings/index.js